import { useState } from "react";
import { cn } from "@/lib/cn";
import { useT } from "@/i18n";
import { panelsHeaderMessages } from "@/i18n/messages/panels-header";
import { Icons } from "@/components/ui/icons";
import { ProjectAvatar } from "@/components/ui/project-avatar";
import {
  ContextMenuItem,
  useContextMenuClose,
} from "@/components/ui/context-menu";
import { ContextMenuSubmenuItem } from "@/components/ui/context-menu-submenu";
import type { BrowseProject } from "./browse-tree";

/* ---------- RenameSubmenuItem — hover to show inline rename input ---------- */

interface RenameSubmenuItemProps {
  currentName: string;
  onSubmit: (name: string) => void;
  maxLength?: number;
  "data-testid"?: string;
}

export function RenameSubmenuItem({
  currentName,
  onSubmit,
  maxLength = 50,
  "data-testid": testId,
}: RenameSubmenuItemProps) {
  const t = useT(panelsHeaderMessages);
  const [draft, setDraft] = useState(currentName);
  const closeMenu = useContextMenuClose();
  const renameTestId = testId ?? "browse-tree-rename-submenu";

  function commit() {
    const trimmed = draft.trim();
    if (trimmed && trimmed !== currentName) {
      onSubmit(trimmed);
    }
    closeMenu();
  }

  return (
    <ContextMenuSubmenuItem
      label={t("rename")}
      icon={<Icons.Edit />}
      menuLabel={t("rename")}
      onOpenChange={(open) => {
        if (open) setDraft(currentName);
      }}
      data-testid={renameTestId}
    >
      <input
        type="text"
        value={draft}
        maxLength={maxLength}
        onChange={(e) => setDraft(e.target.value)}
        onKeyDown={(e) => {
          if (e.key !== "Enter") return;
          e.preventDefault();
          commit();
        }}
        className={cn(
          "w-[calc(var(--appkit-space-xl)*5.5)] rounded-control-sm border bg-glass-fill-heavy px-appkit-2xs py-appkit-2xs outline-none",
          "text-appkit-small text-primary focus:border-accent focus:ring-1",
        )}
        aria-label={t("rename")}
        data-testid={`${renameTestId}-input`}
      />
    </ContextMenuSubmenuItem>
  );
}

/* ---------- ColorSubmenuItem — hover to show swatch flyout ---------- */

export const PRESET_COLORS = [
  "#30D158",
  "#5E5CE6",
  "#FF6B6B",
  "#FF9F0A",
  "#FFD60A",
  "#64D2FF",
  "#BF5AF2",
  "#FF375F",
];

interface ColorSubmenuItemProps {
  currentColor?: string;
  onSelect: (color: string) => void;
  "data-testid"?: string;
}

export function ColorSubmenuItem({
  currentColor,
  onSelect,
  "data-testid": testId,
}: ColorSubmenuItemProps) {
  const t = useT(panelsHeaderMessages);
  const closeMenu = useContextMenuClose();

  return (
    <ContextMenuSubmenuItem
      label={t("setColor")}
      icon={<Icons.Palette />}
      menuLabel={t("setColor")}
      data-testid={testId ?? "browse-tree-color-submenu"}
    >
      <div className="grid w-[calc(var(--appkit-space-xl)*4.75)] grid-cols-4 gap-appkit-sm">
        {PRESET_COLORS.map((color) => (
          <button
            key={color}
            type="button"
            role="menuitem"
            aria-label={color}
            className={cn(
              "flex h-appkit-xl w-appkit-xl items-center justify-center rounded-control transition-transform hover:scale-110 focus:outline-none focus-visible:ring-2 focus-visible:ring-accent",
              color === currentColor &&
                "ring-2 ring-white ring-offset-2 ring-offset-transparent",
            )}
            style={{ backgroundColor: color }}
            onClick={() => {
              onSelect(color);
              closeMenu();
            }}
            data-testid={`color-swatch-${color.replace("#", "")}`}
          >
            {color === currentColor && (
              <Icons.Check className="h-appkit-sm w-appkit-sm drop-shadow-sm text-[color:var(--color-readable-overlay-primary)]" />
            )}
          </button>
        ))}
      </div>
    </ContextMenuSubmenuItem>
  );
}

/* ---------- MoveSubmenuItem — hover to show project list flyout ---------- */

interface MoveSubmenuItemProps {
  candidates: BrowseProject[];
  onSelect: (toProjectId: string) => void;
  "data-testid"?: string;
}

export function MoveSubmenuItem({
  candidates,
  onSelect,
  "data-testid": testId,
}: MoveSubmenuItemProps) {
  const t = useT(panelsHeaderMessages);
  return (
    <ContextMenuSubmenuItem
      label={t("moveToProject")}
      icon={<Icons.ArrowRight />}
      menuLabel={t("moveToProject")}
      disabled={candidates.length === 0}
      flyoutClassName="min-w-[calc(var(--appkit-space-xl)*5)]"
      data-testid={testId ?? "browse-tree-move-submenu"}
    >
      {candidates.map((p) => (
        <ContextMenuItem
          key={p.id}
          icon={
            <ProjectAvatar
              name={p.name}
              color={p.color}
              isHome={p.isHome}
              size="sm"
            />
          }
          onClick={() => onSelect(p.id)}
          data-testid={`browse-tree-move-target-${p.id}`}
        >
          {p.name}
        </ContextMenuItem>
      ))}
    </ContextMenuSubmenuItem>
  );
}
